import React, { useState } from 'react';
import requiredIf from 'react-required-if';
import PropTypes from 'prop-types';

import { useStyles } from '~/assets/styles';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { useRestrictedPermissions } from '~/components/core/Permissions/RestrictedPermissions';
import cn from '~/Utils/cn';

const InlineIconButton = ({
  icon,
  defaultColor = undefined,
  disabled = false,
  className = '',
  iconStyle = {},
  tooltipTitle,
  useIconButton = false,
  ignorePermissions = false,
  ariaLabel: ariaLabelProp = undefined,
  // Tooltip is not interactive by default, as this button is often rendered in a row with
  // hover styles that would cancel out when pointer moves into the bounds of the tooltip.
  interactiveTooltip = false,
  // For a button that opens a popover: the attributes `getPopoverTriggerAria` returns.
  'aria-haspopup': ariaHasPopup = undefined,
  'aria-expanded': ariaExpanded = undefined,
  'aria-controls': ariaControls = undefined,
  ...restProps
}) => {
  const { classes } = useStyles();
  const [isMouseOver, setIsMouseOver] = useState(false);
  const { userHasContextPermissions, permissionDeniedTooltipText } = useRestrictedPermissions();

  const IconComponent = icon;
  const style = { width: 16, height: 16, ...iconStyle };

  const handleMouseEnter = () => setIsMouseOver(true);
  const handleMouseLeave = () => setIsMouseOver(false);

  const ariaLabel = ariaLabelProp || (typeof tooltipTitle === 'string' ? tooltipTitle : undefined);

  const disabledIconElement = (
    <IconComponent style={style} className={cn(className, classes.hoverableIconDisabled)} color="disabled" disabled />
  );

  // A denied icon keeps the element tree of a disabled one, which is that of an enabled one: permissions can change
  // under an open popover (a write freeze), and a popover pinned to the button would lose its anchor were it replaced.
  // So a disabled button stays the same button, disabled, inside the span the tooltip listens on: a disabled button
  // fires no mouse events of its own.
  const isPermissionDenied = !userHasContextPermissions && !ignorePermissions;
  if (disabled || isPermissionDenied) {
    const disabledAriaLabel = isPermissionDenied ? undefined : ariaLabel;
    // color={'disabled'} is backward compatibility for MUI
    // disabled is for our new icons
    // When useIconButton is true, wrap in disabled IconButton to maintain consistent size
    const wrappedContent = useIconButton ? (
      <IconButton disabled className={className} aria-label={disabledAriaLabel}>
        {disabledIconElement}
      </IconButton>
    ) : (
      // A disabled button stays in the accessibility tree, so it keeps its name: an unnamed button says nothing.
      <button type="button" className="button-reset" disabled aria-label={ariaLabel}>
        {disabledIconElement}
      </button>
    );

    return (
      <Tooltip
        title={(isPermissionDenied ? permissionDeniedTooltipText : tooltipTitle) || ''}
        placement="top"
        disableInteractive={!interactiveTooltip}
      >
        <span>{wrappedContent}</span>
      </Tooltip>
    );
  }

  if (useIconButton) {
    return (
      <Tooltip title={tooltipTitle || ''} placement="top" disableInteractive={!interactiveTooltip}>
        <span>
          <IconButton
            aria-label={ariaLabel}
            aria-haspopup={ariaHasPopup}
            aria-expanded={ariaExpanded}
            aria-controls={ariaControls}
            {...restProps}
            className={className}
          >
            <IconComponent className={className} style={style} color={defaultColor ? defaultColor : 'action'} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  // The click and the name go on a real button, so the keyboard can reach it and a popover it opens
  // can return focus to it. With an `href`, they go on a link instead, because a button inside a link
  // is invalid and adds a second Tab stop. Every other prop still styles the icon.
  const { onClick, href, target, rel, ...iconProps } = restProps;
  const iconElement = (
    <IconComponent
      {...iconProps}
      className={className}
      style={{ cursor: 'pointer', ...style }}
      color={isMouseOver ? 'primary' : defaultColor ? defaultColor : 'action'}
    />
  );

  return (
    <Tooltip title={tooltipTitle || ''} placement="top" disableInteractive={!interactiveTooltip}>
      <span>
        {href ? (
          <a
            href={href}
            target={target}
            rel={rel}
            aria-label={ariaLabel}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {iconElement}
          </a>
        ) : (
          <button
            type="button"
            className="button-reset"
            aria-label={ariaLabel}
            aria-haspopup={ariaHasPopup}
            aria-expanded={ariaExpanded}
            aria-controls={ariaControls}
            onClick={onClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {iconElement}
          </button>
        )}
      </span>
    </Tooltip>
  );
};

InlineIconButton.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  defaultColor: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  iconStyle: PropTypes.object,
  onClick: requiredIf(PropTypes.func, (props) => !props.disabled && !props.href),
  // Renders a link instead of a button. Ignored while disabled, which renders the disabled button.
  href: PropTypes.string,
  target: PropTypes.string,
  rel: PropTypes.string,
  tooltipTitle: PropTypes.string,
  useIconButton: PropTypes.bool,
  ignorePermissions: PropTypes.bool,
  ariaLabel: PropTypes.string,
  interactiveTooltip: PropTypes.bool,
  'aria-haspopup': PropTypes.oneOf(['dialog']),
  'aria-expanded': PropTypes.bool,
  'aria-controls': PropTypes.string,
};

export default InlineIconButton;
